import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkImportModal = ({ onClose, onImport, departments, roles }) => {
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [step, setStep] = useState('upload'); // upload, preview, confirm
  const [fileError, setFileError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile) {
      // Check if file is CSV
      if (!selectedFile?.name?.endsWith('.csv')) {
        setFileError('Only CSV files are supported. Please upload a .csv file.');
        setFile(null);
        return;
      }
      setFileError('');
      setFile(selectedFile);
      parseFile(selectedFile);
    }
  };

  const parseFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e?.target?.result;
        const lines = text?.split('\n')?.filter(line => line?.trim());
        
        if (lines?.length < 2) {
          setFileError('File must contain at least a header row and one data row');
          setErrors([]);
          return;
        }

        const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase());
        const requiredHeaders = ['name', 'email', 'phone', 'department', 'role', 'hiredate', 'status'];
        const missingHeaders = requiredHeaders?.filter(h => !headers?.includes(h));

        if (missingHeaders?.length > 0) {
          setFileError(`Missing required columns: ${missingHeaders?.join(', ')}. Please download the template for correct format.`);
          setErrors([]);
          return;
        }

        // Create case-insensitive lookup maps
        const departmentMap = new Map();
        departments?.forEach(dept => {
          departmentMap?.set(dept?.toLowerCase(), dept);
        });

        const roleMap = new Map();
        roles?.forEach(role => {
          roleMap?.set(role?.toLowerCase(), role);
        });

        const data = [];
        const validationErrors = [];

        for (let i = 1; i < lines?.length; i++) {
          const values = lines?.[i]?.split(',')?.map(v => v?.trim());
          const row = {};
          
          headers?.forEach((header, index) => {
            row[header] = values?.[index] || '';
          });

          // Validation
          const rowErrors = [];
          if (!row?.name) rowErrors?.push('Name is required');
          if (!row?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(row?.email)) {
            rowErrors?.push('Valid email is required');
          }
          if (!row?.phone) rowErrors?.push('Phone is required');
          
          // Case-insensitive department matching
          const matchedDepartment = departmentMap?.get(row?.department?.toLowerCase());
          if (!row?.department || !matchedDepartment) {
            rowErrors?.push(`Department "${row?.department}" not found. Valid departments: ${departments?.join(', ')}`);
          }
          
          // Case-insensitive role matching
          const matchedRole = roleMap?.get(row?.role?.toLowerCase());
          if (!row?.role || !matchedRole) {
            rowErrors?.push(`Role "${row?.role}" not found. Valid roles: ${roles?.join(', ')}`);
          }
          
          if (!row?.hiredate) {
            rowErrors?.push('Hire date is required');
          } else {
            // Validate date format
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex?.test(row?.hiredate)) {
              rowErrors?.push('Hire date must be in YYYY-MM-DD format');
            }
          }

          if (rowErrors?.length > 0) {
            validationErrors?.push({ row: i, errors: rowErrors });
          }

          data?.push({
            name: row?.name,
            email: row?.email,
            phone: row?.phone,
            department: matchedDepartment || row?.department,
            role: matchedRole || row?.role,
            hireDate: row?.hiredate,
            status: row?.status || 'Active',
            hasErrors: rowErrors?.length > 0,
            errors: rowErrors
          });
        }

        setPreviewData(data);
        setErrors(validationErrors);
        setFileError('');
        setStep('preview');
      } catch (error) {
        setFileError('Error parsing CSV file. Please ensure the file is properly formatted.');
        setErrors([]);
        console.error('CSV parsing error:', error);
      }
    };

    reader.onerror = () => {
      setFileError('Error reading file. Please try again.');
    };

    reader?.readAsText(file);
  };

  const handleImport = () => {
    const validData = previewData?.filter(row => !row?.hasErrors);
    if (validData?.length > 0) {
      // Remove validation properties before importing to database
      const cleanData = validData?.map(({ hasErrors, errors, ...employee }) => employee);
      onImport(cleanData);
    }
  };

  const downloadTemplate = () => {
    const template = 'name,email,phone,department,role,hiredate,status\nJohn Doe,john.doe@company.com,(555) 123-4567,Operations,Operations Manager,2024-01-15,Active';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employee_import_template.csv';
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Bulk Import Employees</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Upload a CSV file to import multiple employees at once
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={24} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 'upload' && (
            <div className="space-y-6">
              {fileError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="XCircle" size={20} className="text-red-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-red-900 mb-1">File Error</h4>
                      <p className="text-sm text-red-800">{fileError}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Icon name="Upload" size={48} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Upload CSV File</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Drag and drop your file here, or click to browse
                </p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button type="button" onClick={() => document.getElementById('file-upload')?.click()}>
                    Select File
                  </Button>
                </label>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">File Requirements</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• File must be in CSV format (.csv extension)</li>
                      <li>• Required columns: name, email, phone, department, role, hiredate, status</li>
                      <li>• Department and role names are case-insensitive</li>
                      <li>• Valid departments: {departments?.join(', ')}</li>
                      <li>• Valid roles: {roles?.join(', ')}</li>
                      <li>• Date format: YYYY-MM-DD (e.g., 2024-01-15)</li>
                    </ul>
                    <button
                      onClick={downloadTemplate}
                      className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-700 underline"
                    >
                      Download Template File
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'preview' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Preview Import Data</h3>
                  <p className="text-sm text-muted-foreground">
                    {previewData?.length} rows found, {previewData?.filter(r => !r?.hasErrors)?.length} valid, {errors?.length} with errors
                  </p>
                </div>
                <Button variant="outline" onClick={() => setStep('upload')}>
                  <Icon name="Upload" size={16} className="mr-2" />
                  Upload Different File
                </Button>
              </div>

              {errors?.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-yellow-900 mb-2">Validation Errors Found</h4>
                      <p className="text-sm text-yellow-800 mb-3">
                        {errors?.length} row(s) contain errors and will be skipped during import.
                      </p>
                      <div className="max-h-32 overflow-y-auto space-y-2">
                        {errors?.slice(0, 5)?.map((error, idx) => (
                          <div key={idx} className="text-xs text-yellow-700 bg-yellow-100 rounded p-2">
                            <strong>Row {error?.row}:</strong> {error?.errors?.join(', ')}
                          </div>
                        ))}
                        {errors?.length > 5 && (
                          <p className="text-xs text-yellow-700 italic">...and {errors?.length - 5} more errors</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="border border-border rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-96">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b border-border sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Name</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Department</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Role</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Hire Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {previewData?.map((row, index) => (
                        <tr key={index} className={row?.hasErrors ? 'bg-red-50' : 'hover:bg-muted/30'}>
                          <td className="px-4 py-3">
                            {row?.hasErrors ? (
                              <div className="flex items-center gap-2 text-red-600" title={row?.errors?.join(', ')}>
                                <Icon name="XCircle" size={16} />
                                <span className="text-xs font-medium">Error</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-green-600">
                                <Icon name="CheckCircle" size={16} />
                                <span className="text-xs font-medium">Valid</span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-foreground">{row?.name}</td>
                          <td className="px-4 py-3 text-sm text-foreground">{row?.email}</td>
                          <td className="px-4 py-3 text-sm text-foreground">{row?.department}</td>
                          <td className="px-4 py-3 text-sm text-foreground">{row?.role}</td>
                          <td className="px-4 py-3 text-sm text-foreground">{row?.hireDate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {step === 'preview' && (
            <Button
              onClick={handleImport}
              disabled={previewData?.filter(r => !r?.hasErrors)?.length === 0}
            >
              Import {previewData?.filter(r => !r?.hasErrors)?.length} Employee(s)
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImportModal;